
import type { Ref } from 'vue';
import type { SearchType } from './useProductOverviewUI';
import type { Praeparat } from '@/types/otc/praeparat';
import { SearchHistory } from '@/classes/searchHistory';
import SearchHistoryService from '@/services/searchHistoryService';
import { emitterInstance } from '@/classes/emitter';

/**
 * Verwaltet alle Benutzerinteraktionen und Navigation in der Product Overview
 * Zuständig für Suche, Filter, Navigation und externe Links
 */
export function useProductOverviewActions(
    searchType: Ref<SearchType>,
    selectedLetter: Ref<string>,
    openCategories: Ref<Set<number>>,
    setSearch: (keyword: string) => Promise<void>,
    setLetter: (letter: string) => void,
    toggleCategory: (id: string)=> void,
    resetToDefault: () => void,
    loadProducts: ()=>Promise<boolean>
) {
    let searchTerm = "";
    /**
     * Verarbeitet Sucheingaben und wechselt entsprechend den Anzeigemodus
     */
    const onSearch = async (keyword: string) => {
        if (keyword.trim()) {
            searchType.value = 'default';
        } else {
            searchType.value = 'alphabetisch';
        }
        searchTerm = keyword;
        await setSearch(keyword);
    };

    /**
     * Verarbeitet Buchstabenauswahl im alphabetischen Filter
     */
    const onSelectLetter = (letter: string) => {
        searchType.value = 'alphabetisch';
        setLetter(letter);
    };

    /**
     * Wechselt zur alphabetischen Ansicht und setzt Filter zurück
     */
    const renderAlphabetisch = async() => {
        searchType.value = 'alphabetisch';
        await resetToDefault();
    };

    /**
     * Wechselt zur Kategorielisten-Ansicht und setzt Filter zurück
     */
    const renderListe = async() => {
        loadProducts();
        searchType.value = 'liste';
        await resetToDefault();
    };

    /**
     * Navigiert zur Produktdetailseite
     */
    const onReadMore = (props:{product: Praeparat, searchterm:string}) => {
        setSearchHistory(props.product.id, props.searchterm);
        const url = props.product.url? props.product.url :`/praeparat/${props.product.id}`;
        window.location.href = url;
    };

    /**
     * Öffnet den Apotheken-Finder mit den übergebenen PZN-Nummern
     */
    const onOrder = (pzns: string[]) => {
        if (!pzns.length) return;

        return pzns.join(',');
    };

    /**
     * Öffnet die Pflichttext-Seite für ein Produkt
     */
    const onPflichttext = (produktId: number, praeparatId: number, searchterm:string="") => { 
        setSearchHistory(praeparatId,searchterm);
        const url = `pflichttext?product=${produktId}&praeparat=${praeparatId}`;
        window.open(url, '_self');
    };

    const setSearchHistory = (productId:number, searchterm:string)=>{
        const searchHistory = new SearchHistory({
            historyType : searchterm && searchterm.length>0 ? "praeparatSearch":"praeparat",
            searchType: searchType.value,
            contentShowOverlay: false,
            id: productId,
            searchTerm: searchterm,
            productFilter: searchType.value == "liste"? Array.from(openCategories.value).join(';'): selectedLetter.value
        });
        SearchHistoryService.Instance.setSearchHistory(searchHistory);
    }

    const reloadSearchHistory = async():Promise<boolean> => {
        const searchHistory = SearchHistoryService.Instance.getSearchHistory();
        if(searchHistory.historyType.startsWith("praeparat")){
            switch(searchHistory.searchType){
                case "default":
                    emitterInstance.emit('updateSearchTerm', searchHistory.searchTerm);
                    await onSearch(searchHistory.searchTerm);
                    break;
                case "alphabetisch":   
                    await renderAlphabetisch();
                    onSelectLetter(searchHistory.productFilter);
                    break;
                case "liste": 
                    await renderListe();
                    const filter = searchHistory.productFilter;
                    if(filter && filter.length >0){
                        filter.split(";").forEach( idString => {
                            toggleCategory(idString);
                        })
                    }
                    break;
            }
            setTimeout(function(){                    
                    const clickedElement = document.getElementById(searchHistory.id);
                    if(clickedElement){
                        clickedElement.scrollIntoView({behavior: 'smooth'});
                    }
                }, 1000);
            SearchHistoryService.Instance.removeSearchHistory();  
            return true;   
        }   
        return false;
    }

    return {
        onSearch,
        onSelectLetter,
        renderAlphabetisch,
        renderListe,
        onReadMore,
        onOrder,
        onPflichttext,
        setSearchHistory,
        reloadSearchHistory
    };
}